import { ExternalLink, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

export const MAX_SOURCE_LINKS = 10

/** Same rule the API applies, asked here so the answer arrives before the save does. */
function looksLikeALink(value: string): boolean {
  if (!value.trim()) return true // an empty row is being typed, not wrong
  try {
    const url = new URL(value.trim())
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Where this came from — up to ten links.
 *
 * One row to start, an Add button underneath, and a row disappears when you clear it on the
 * way out. The alternative — ten empty boxes, or a textarea people paste newline-separated
 * links into — either shouts about a limit nobody is near or produces text that cannot be
 * clicked.
 *
 * A filled row shows an open-in-new-tab button, because the common use is not editing the
 * link, it is following it.
 */
export function SourceLinksField({
  value,
  onChange,
  label = 'Source links',
  hint = 'The ticket, thread, doc or recording this came from.',
}: {
  value: string[]
  onChange: (next: string[]) => void
  label?: string
  hint?: string
}) {
  // Always one row to type into, so the field is never a button with nothing above it.
  const rows = value.length === 0 ? [''] : value

  const setAt = (index: number, next: string) => {
    const copy = [...rows]
    copy[index] = next
    onChange(copy)
  }

  const removeAt = (index: number) => {
    const copy = rows.filter((_, i) => i !== index)
    onChange(copy)
  }

  return (
    <div className="grid gap-1.5">
      <Label>
        {label}
        <span className="text-text-muted"> — optional</span>
      </Label>

      <div className="grid gap-1.5">
        {rows.map((link, index) => {
          const valid = looksLikeALink(link)
          const filled = link.trim().length > 0
          return (
            <div key={index} className="flex items-center gap-1.5">
              <Input
                value={link}
                onChange={(e) => setAt(index, e.target.value)}
                placeholder="https://…"
                aria-invalid={!valid}
                aria-label={`${label} ${index + 1}`}
                className={cn('h-8 min-w-0 flex-1 text-xs', !valid && 'border-danger')}
              />
              {filled && valid ? (
                <a
                  href={link.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="grid size-8 shrink-0 place-items-center rounded-md text-text-muted transition-colors dur-fast hover:bg-muted hover:text-brand"
                  aria-label={`Open link ${index + 1} in a new tab`}
                  title="Open in a new tab"
                >
                  <ExternalLink className="size-3.5" />
                </a>
              ) : (
                <span className="size-8 shrink-0" aria-hidden />
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0"
                onClick={() => removeAt(index)}
                disabled={rows.length === 1 && !filled}
                aria-label={`Remove link ${index + 1}`}
              >
                <X className="size-3.5" />
              </Button>
            </div>
          )
        })}
      </div>

      {rows.some((l) => !looksLikeALink(l)) ? (
        <p className="text-tiny text-danger">
          A link should start with http:// or https://.
        </p>
      ) : (
        <p className="text-tiny text-text-muted">{hint}</p>
      )}

      {rows.length < MAX_SOURCE_LINKS ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 justify-self-start gap-1 px-2 text-tiny text-brand"
          onClick={() => onChange([...rows, ''])}
          // A row that is still empty is the row they should be typing in.
          disabled={rows.some((l) => !l.trim())}
        >
          <Plus className="size-3.5" />
          Add another link
        </Button>
      ) : (
        <p className="text-tiny text-text-muted">That is all ten.</p>
      )}
    </div>
  )
}

/** Drop the empty rows the field keeps for typing into. Use this when submitting. */
export const cleanSourceLinks = (links: string[]): string[] =>
  [...new Set(links.map((l) => l.trim()).filter(Boolean))]
